import { CommonModule } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { StepperModule } from 'primeng/stepper';
import { ToastModule } from 'primeng/toast';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { CheckboxChangeEvent, CheckboxModule } from 'primeng/checkbox';
import { ActivatedRoute, Router } from '@angular/router';
import { Routes } from 'src/app/core/routes/routes';
import { UserApi } from '@features/user/services/user-api';
import { BusinessApi } from '@features/business/services/business-api';
import { DataEndpoint } from '@core/models/general.model';
import { UserDetailModel, UserCreateRequestModel, UserBusinessModel } from '@features/user/models/user.model';
import { BusinessGroupedModel, BusinessModel, OfficeModel } from '@features/business/models/business.model';
import { HttpErrorResponse } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { SelectModule } from 'primeng/select';
import { MultiSelect } from "primeng/multiselect";
import { FormFooter } from "@shared/components/form-footer/form-footer";
import { BusinessLogo } from "@shared/components/business-logo/business-logo";
import { AppDialog } from "@shared/components/dialog";
import { BlockUI } from "primeng/blockui";
import { ProgressSpinner } from "primeng/progressspinner";

@Component({
  selector: 'app-user-form',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    InputTextModule,
    CheckboxModule,
    ButtonModule,
    MessageModule,
    StepperModule,
    ToastModule,
    SelectModule,
    MultiSelect,
    FormFooter,
    BusinessLogo,
    AppDialog,
    BlockUI,
    ProgressSpinner
],
  templateUrl: './user-form.html',
  styleUrl: './user-form.scss',
  providers: [MessageService]
})
export class UserForm {
  readonly router = inject(Router);
  readonly route = inject(ActivatedRoute);
  readonly messageService = inject(MessageService);
  readonly userApi = inject(UserApi);
  readonly businessApi = inject(BusinessApi);

  isEditMode = signal(false);

  form: FormGroup = new FormGroup({
    userId: new FormControl(''),
    name: new FormControl('', [Validators.required]),
    lastName: new FormControl('', [Validators.required]),
    email: new FormControl('', [Validators.required, Validators.email]),
    phone: new FormControl('', []),
    isAdmin: new FormControl(false),
    selectedBusinessGroup: new FormControl('', [Validators.required]),
    selectedBusinesses: new FormControl<string[]>([], [Validators.required]),
    selectedOffices: new FormControl<string[]>([], [Validators.required]),
    businesses: new FormControl<UserBusinessModel[]>([]),
  });

  businessGroupOptions: BusinessGroupedModel[] = [];
  availableBusinesses: BusinessModel[] = [];
  availableOffices: OfficeModel[] = [];

  userId: string | null = null;
  isFormSubmitted = false;
  isLoading = false;
  activeStep: number = 1;

  visibleSuccessDialog: boolean = false;
  dialogTitle: string = '';
  dialogMessage: string = '';

  // Permisos disponibles
  permissionsOptions = [
    {
      id: 'sedes',
      label: 'Sedes',
      icon: 'pi pi-map-marker',
      permissions: [
        { code: 'CREATE_OFFICE', name: 'Crear sede', description: 'Permite crear sedes' },
        { code: 'UPDATE_OFFICE', name: 'Actualizar sede', description: 'Permite actualizar sedes' },
        { code: 'DELETE_OFFICE', name: 'Cambiar estado sede', description: 'Permite cambiar estado de sedes' },
      ]
    },
    {
      id: 'actividades',
      label: 'Actividades',
      icon: 'pi pi-list',
      permissions: [
        { code: 'CREATE_ACTIVITY', name: 'Crear actividad', description: 'Permite crear actividades' },
        { code: 'UPDATE_ACTIVITY', name: 'Actualizar actividad', description: 'Permite actualizar actividades' }
      ]
    }
  ];

  selectedPermission: string = 'sedes';
  permissionSelections: Record<string, boolean> = {};

  ngOnInit() {
    this.loadBusinessGroups();
  }

  loadBusinessGroups() {
    this.businessApi.getBusinessGrouped().subscribe({
      next: (response: DataEndpoint<BusinessGroupedModel[]>) => {
        if (response.Status === 200) {
          this.businessGroupOptions = response.Data || [];
          this.checkMode();
        }
      },
      error: (error: HttpErrorResponse) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error?.error.Message
        });
      }
    });
  }

  onBusinessGroupChange(selectedGroupId: string) {
    // Limpiar selecciones de empresas y sedes al cambiar el grupo
    this.form.patchValue({
      selectedBusinesses: [],
      selectedOffices: []
    });
    this.availableBusinesses = [];
    this.availableOffices = [];

    if (!selectedGroupId) {
      return;
    }

    // Filtrar empresas según grupo seleccionado
    this.updateAvailableBusinesses(selectedGroupId);
  }

  // Obtener ids de empresas de acuerdo con grupo seleccionado
  updateAvailableBusinesses(selectedGroupId: string) {
    if (!selectedGroupId) {
      this.availableBusinesses = [];
      this.availableOffices = [];
      return;
    }

    this.availableBusinesses = this.businessGroupOptions
      .filter(group => selectedGroupId === group.Id)
      .flatMap(group => group.Business || []);
  }

  onBusinessChange(selectedBusinessIds: string[]) {
    // Al eliminar las empresas seleccionadas limpiar el listado de sedes
    if (selectedBusinessIds.length === 0) {
      this.form.patchValue({
        selectedBusinesses: [],
        selectedOffices: []
      });
      this.availableOffices = [];
      return;
    }

    const isWithoutGroup = this.form.get('selectedBusinessGroup')?.value === '-1';

    // Si el grupo es "-1 - Sin grupo", solo permitir una empresa
    if (isWithoutGroup && selectedBusinessIds.length > 1) {
      const lastSelected = selectedBusinessIds[selectedBusinessIds.length - 1];
      selectedBusinessIds = [lastSelected];
    }

    this.form.patchValue({ selectedBusinesses: selectedBusinessIds });

    // Actualizar sedes disponibles según empresas seleccionadas
    this.updateAvailableOffices(selectedBusinessIds);

    // Limpiar selección de sedes si cambió la selección de empresas
    const currentOffices = this.form.get('selectedOffices')?.value || [];
    const validOffices = currentOffices.filter((officeId: string) =>
      this.availableOffices.some(o => o.Id === officeId)
    );
    this.form.patchValue({ selectedOffices: validOffices });
  }

  // Obtener ids de sedes de acuerdo con empresas seleccionadas
  updateAvailableOffices(selectedBusinessIds: string[]) {
    if (!selectedBusinessIds || selectedBusinessIds.length === 0) {
      this.availableOffices = [];
      return;
    }

    this.availableOffices = this.availableBusinesses
      .filter(business => selectedBusinessIds.includes(business.Id))
      .flatMap(business => business.Offices || []);
  }

  buildBusinessesArray(): UserBusinessModel[] {
    const selectedBusinessIds = this.form.get('selectedBusinesses')?.value || [];
    const selectedOfficeIds = this.form.get('selectedOffices')?.value || [];

    if (selectedBusinessIds.length === 0) {
      return [];
    }

    // Agrupar sedes por empresa
    const businessesMap = new Map<string, string[]>();

    // Para cada empresa seleccionada
    selectedBusinessIds.forEach((businessId: string) => {
      const business = this.availableBusinesses.find(b => b.Id === businessId);
      if (business) {
        // Obtener las sedes de esta empresa que están seleccionadas
        const businessOffices = (business.Offices || [])
          .filter(office => selectedOfficeIds.includes(office.Id))
          .map(office => office.Id);

        if (businessOffices.length > 0) {
          businessesMap.set(businessId, businessOffices);
        }
      }
    });

    // Convertir el mapa a array de UserBusinessModel
    return Array.from(businessesMap.entries()).map(([businessId, officesIds]) => ({
      BusinessId: businessId,
      OfficesIds: officesIds
    }));
  }

  // Si el usuario NO es admin se debe validar grupo empresarial, empresa y sede
  handleIsAdmin(event: CheckboxChangeEvent) {
    const controls = [
      this.form.get('selectedBusinessGroup'),
      this.form.get('selectedBusinesses'),
      this.form.get('selectedOffices')
    ];

    if (event.checked) {
      controls.forEach(control => {
        if (!control) return;

        // Elimina validadores y errores
        control.removeValidators(Validators.required);
        control.setErrors(null);

        control.markAsUntouched();
        control.markAsPristine();

        // Actualiza el estado
        control.updateValueAndValidity();
      });

      // Limpiar selecciones de grupo, empresas y sedes para admins
      this.form.patchValue({
        selectedBusinessGroup: '',
        selectedBusinesses: [],
        selectedOffices: [],
        businesses: []
      });
      this.availableBusinesses = [];
      this.availableOffices = [];
    } else {
      controls.forEach(control => {
        if (!control) return;

        // Agregar nuevamente el validador required
        control.addValidators(Validators.required);
        control.updateValueAndValidity();
      });
    }
  }

  getBusinessGroupOptions() {
    return this.businessGroupOptions.map(group => ({
      label: group.Name,
      value: group.Id,
      logoUrl: group.LogoUrl
    }));
  }

  getBusinessOptions() {
    return this.availableBusinesses.map(business => ({
      label: business.Name,
      value: business.Id,
      logoUrl: business.LogoUrl
    }));
  }

  getOfficeOptions() {
    // Crear estructura agrupada por empresa
    const groupedOptions: any[] = [];

    // Solo incluir empresas que tienen sedes disponibles
    this.availableBusinesses.forEach(business => {
      if (business.Offices && business.Offices.length > 0) {
        // Filtrar solo las sedes que están disponibles para las empresas seleccionadas
        const businessOffices = business.Offices.filter(office =>
          this.availableOffices.some(availableOffice => availableOffice.Id === office.Id)
        );

        if (businessOffices.length > 0) {
          groupedOptions.push({
            label: business.Name,
            logoUrl: business.LogoUrl,
            value: business.Id,
            items: businessOffices.map(office => ({
              label: office.Name,
              value: office.Id
            }))
          });
        }
      }
    });

    return groupedOptions;
  }

  isBusinessSelectDisabled(): boolean {
    const selectedGroup = this.form.get('selectedBusinessGroup')?.value;
    return !selectedGroup;
  }

  isBusinessSelectMultiple(): boolean {
    const selectedGroup = this.form.get('selectedBusinessGroup')?.value;
    // Si seleccionó "-1", el select solo debe permitir seleccionar una empresa
    return selectedGroup !== '-1';
  }

  private checkMode(): void {
    this.userId = this.route.snapshot.paramMap.get('id');

    if (this.userId) {
      this.isEditMode.set(true);
      this.loadUserData(this.userId);
    } else {
      this.isEditMode.set(false);
    }
  }

  private loadUserData(id: string): void {
    this.isLoading = true;

    this.userApi.getUserById(id).subscribe({
      next: (response: DataEndpoint<UserDetailModel>) => {
        if (response.Status === 200) {
          this.setEditForm(response.Data!);
        }
        this.isLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error?.error.Message,
          life: 3000
        });
        this.isLoading = false;
      }
    });
  }

  private setEditForm(data: UserDetailModel) {
    // Determinar si el usuario es admin
    const isAdmin = data.Role?.toLowerCase() === 'admin';
    // Mapear datos básicos
    this.form.patchValue({
      userId: data.Id,
      name: data.Name,
      lastName: data.LastName,
      email: data.Email,
      phone: data.Phone,
      isAdmin: isAdmin
    });

    // Si no es admin, mapear grupo empresarial, empresas, sedes y permisos
    if (!isAdmin && data.BusinessGroupId && data.Businesses.length > 0) {
      this.mapBusinessAssignments(data);
    } else if (!isAdmin) {
      // Si no es admin pero no tiene businesses, aplicar validadores
      this.handleIsAdmin({ checked: false } as CheckboxChangeEvent);
    } else {
      // Si es admin, remover validadores
      this.handleIsAdmin({ checked: true } as CheckboxChangeEvent);
    }

    // Mapear permisos si no es admin y tiene permisos
    if (!isAdmin && data.GrantedPermissions && data.GrantedPermissions.length > 0) {
      this.loadPermissionsFromData(data.GrantedPermissions);
    }
  }

  private mapBusinessAssignments(data: UserDetailModel) {
    const selectedGroupId: string = data.BusinessGroupId;
    const selectedBusinessIds: string[] = [];
    const selectedOfficeIds: string[] = [];
    this.updateAvailableBusinesses(selectedGroupId);

    data.Businesses.forEach((business) => {
      selectedBusinessIds.push(business.BusinessId)
      business.Offices.forEach((office) => {
        selectedOfficeIds.push(office.OfficeId)
      })
    })

    this.updateAvailableOffices(selectedBusinessIds);

    this.form.patchValue({
      selectedBusinessGroup: selectedGroupId,
      selectedBusinesses: selectedBusinessIds,
      selectedOffices: selectedOfficeIds
    });

    return
  }

  // Validación del formulario antes de avanzar
  canGoNext(): boolean {
    return this.form.valid;
  }

  // Avanzar al siguiente paso
  onClickNextStep(): void {
    if (!this.canGoNext()) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.activeStep < 3) {
      if (this.activeStep === 1) {
        const isAdmin = this.form.get('isAdmin')?.value === true;
        
        if (isAdmin) {
          // Si es admin, construir businesses vacío y guardar directamente
          const businessesArray = this.buildBusinessesArray();
          this.form.patchValue({ businesses: businessesArray });
          this.onSaveForm();
          return;
        }
        
        // Si no es admin, avanzar al paso 2
        this.activeStep = 2;
        return;
      }

      if (this.activeStep === 2) {
        // Construir el array de businesses antes de guardar
        const businessesArray = this.buildBusinessesArray();
        this.form.patchValue({ businesses: businessesArray });

        // Guardar y dejar que el éxito del request mueva a paso 3
        this.onSaveForm();
        return;
      }

      this.activeStep += 1;
    }
  }

  // Retroceder al paso anterior
  onClickPreviousStep(): void {
    if (this.activeStep > 1) {
      const isAdmin = this.form.get('isAdmin')?.value === true;
      // Si está en el paso 3 y es admin, regresar al paso 1
      if (this.activeStep === 3 && isAdmin) {
        this.activeStep = 1;
      } else {
        this.activeStep--;
      }
    }
  }

  onClickCancel(): void {
    this.router.navigate([Routes.usersPage]);
  }

  // Helpers para deshabilitar botones
  isFirstStep(): boolean {
    return this.activeStep === 1;
  }

  isLastStep(): boolean {
    return this.activeStep === 3;
  }

  onSaveForm() {
    if (this.form.valid) {
      const isAdmin = this.form.get('isAdmin')?.value === true;
      const grantedPermissions = isAdmin
        ? []
        : this.getAllSelectedPermissionCodes();

      const request: UserCreateRequestModel = {
        Name: this.form.get('name')?.value,
        LastName: this.form.get('lastName')?.value,
        Email: this.form.get('email')?.value,
        Phone: this.form.get('phone')?.value,
        IsAdmin: this.form.get('isAdmin')?.value || false,
        Businesses: this.form.get('businesses')?.value || [],
        GrantedPermissions: grantedPermissions
      };

      this.isLoading = true;

      if (this.isEditMode()) {
        this.updateUser(request);
      } else {
        this.createUser(request);
      }
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'El formulario es inválido. Por favor, revisar.' });
    }
  }

  private createUser(request: UserCreateRequestModel) {
    this.userApi.createUser(request).subscribe({
      next: (response: DataEndpoint<any>) => {
        if (response.Success) {
          this.activeStep++;
          this.showSuccessDialog();
        }
        this.isLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error?.error.Message });
      }
    });
  }

  private updateUser(request: UserCreateRequestModel) {
    this.userApi.updateUser(this.userId!, request).subscribe({
      next: (response: DataEndpoint<any>) => {
        if (response.Success) {
          this.activeStep++;
          this.showSuccessDialog();
        }
        this.isLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error?.error.Message });
      }
    });
  }

  showSuccessDialog() {
    this.dialogTitle = this.isEditMode()
      ? '¡Usuario actualizado!'
      : '¡Usuario creado!';
    this.dialogMessage = this.isEditMode()
      ? 'El usuario ha sido actualizado exitosamente.'
      : 'El usuario ha sido creado exitosamente.';
    this.visibleSuccessDialog = true;
  }

  onDialogButtonClick() {
    this.visibleSuccessDialog = false;
    this.router.navigate([Routes.usersPage]);
  }

  isInvalid(controlName: string) {
    const control = this.form.get(controlName);
    return control?.invalid && (control.touched || this.isFormSubmitted);
  }

  getErrorMessage(controlName: string): string {
    const control = this.form.get(controlName);
    if (control?.hasError('required')) {
      return 'El campo es obligatorio.';
    }
    if (control?.hasError('email')) {
      return 'El formato del correo electrónico no es válido.';
    }
    return '';
  }

  onPermissionSelect(permissionId: string): void {
    this.selectedPermission = permissionId;
  }

  getSelectedPermissionLabel(): string {
    const permission = this.permissionsOptions.find(p => p.id === this.selectedPermission);
    return permission?.label || '';
  }

  getSelectedPermissionsList(): any[] {
    const permission = this.permissionsOptions.find(p => p.id === this.selectedPermission);
    return permission?.permissions || [];
  }

  // Marcar/desmarcar un permiso sin perder el estado al cambiar de módulo
  onPermissionToggle(code: string, event: CheckboxChangeEvent): void {
    this.permissionSelections[code] = event.checked;
  }

  // Consultar si un permiso está seleccionado
  isPermissionSelected(code: string): boolean {
    return !!this.permissionSelections[code];
  }

  // Utilidad para obtener todos los códigos seleccionados
  getAllSelectedPermissionCodes(): string[] {
    return Object.keys(this.permissionSelections).filter(k => this.permissionSelections[k]);
  }

  // Cargar permisos desde los datos del usuario para modo edición
  loadPermissionsFromData(grantedPermissions: string[]): void {
    // Limpiar selecciones previas
    this.permissionSelections = {};
    
    // Marcar cada permiso como seleccionado
    grantedPermissions.forEach(code => {
      this.permissionSelections[code] = true;
    });
  }
}
